import React, { useEffect } from 'react';
import { Link } from 'gatsby';

import { scrollToElement } from 'helpers';

import { Navigation } from '../../interfaces';
import { URLEnums } from '../../enum/URLEnum';

interface Props {
  menu: Map<Navigation, Navigation[]>;
}

export const DesktopMenu = ({ menu }: Props) => {
  const [openedIndex, setOpenedIndex] = React.useState(-1);
  const [openedMenu, setOpenedMenu] = React.useState(false);
  const menuIsShown = openedIndex !== -1 && openedMenu;
  const hoverDelay = 200;
  let setHoverTimeout: ReturnType<typeof setTimeout>;

  function closeMenu() {
    setOpenedIndex(-1);
    setOpenedMenu(!openedMenu);
    clearTimeout(setHoverTimeout);
  }

  function hoverItem(value: Navigation[], index: number) {
    if (value.length) {
      setHoverTimeout = setTimeout(() => {
        setOpenedIndex(index);
        setOpenedMenu(true);
      }, hoverDelay);
    } else {
      closeMenu();
    }
  }

  function hasChildrenClass(value: Navigation[]) {
    if (value.length) {
      return 'main-navigation-item has-children';
    }

    return 'main-navigation-item no-children';
  }

  if (typeof window !== 'undefined') {
    window.onresize = () => {
      if (window.innerWidth < 1024 && openedMenu) {
        closeMenu();
      }
    };
  }

  useEffect(() => {
    if ('ontouchstart' in document.documentElement) {
      document.body.classList.add('touch-device');
    }
  }, [openedIndex]);

  const isHomePage = () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';

    return url.split('/').at(-1) === '' || url.split('/').at(-1)?.includes('#');
  };

  return (
    <div className="main-navigation-desktop-view">
      <nav className="main-navigation">
        {Array.from(menu).map(([key, value], index) => {
          return (
            <div
              key={key.id}
              className={hasChildrenClass(value)}
              onMouseEnter={() => hoverItem(value, index)}
              onMouseLeave={() => clearTimeout(setHoverTimeout)}
              onClick={() => closeMenu()}
            >
              {key.link ? (
                <a
                  href={key.link.uri}
                  className="main-navigation-link"
                  onClick={(e) => {
                    if (isHomePage() && key.link?.anchor) {
                      scrollToElement(key.link?.anchor || '#flow', e);
                    }
                  }}
                >
                  {key.title}
                </a>
              ) : (
                <div className="main-navigation-menu-container">
                  <p className="main-navigation-link">{key.title}</p>

                  <div
                    className={
                      menuIsShown && index === openedIndex
                        ? `main-navigation-menu opened opened-${openedIndex}`
                        : 'main-navigation-menu'
                    }
                    onMouseEnter={() => setOpenedMenu(true)}
                    onMouseLeave={() => closeMenu()}
                    onClick={() => closeMenu()}
                  >
                    {openedIndex !== -1 &&
                      Array.from(menu.values())[openedIndex].map((item) => {
                        return item.link?.uri.startsWith('/') ? (
                          <Link
                            key={item.id}
                            to={item.link?.uri || ''}
                            target="_self"
                            className="link"
                            onClick={() => closeMenu()}
                            rel="noreferrer"
                            aria-label={`See more how to ${item.title}`}
                          >
                            {item.title}
                          </Link>
                        ) : (
                          <a
                            href={item.link?.uri}
                            key={item.id}
                            className="link"
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => closeMenu()}
                            aria-label={`Go to ${item.title}`}
                          >
                            {item.title}
                          </a>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <a className="main-navigation-link link-button" href={URLEnums.ContactUs} aria-label="Contact us">
          Contact us
        </a>
      </nav>
    </div>
  );
};
